import { WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { buildWabunkaExperienceListViewModel } from '@/models/wabunka/wabunkaExperience';
import { useAsyncFetchFromPublicApi } from '@/wabunka-src/common/composables/query/useAsyncFetchFromPublicApi';
import type { ArticleSection, Contents } from '@/wabunka-src/common/features/Featured/models/featured/featured';

/**
 * ArticleSection 配列から体験スラッグを重複なく抽出する
 *
 * contents には ArticleSection 以外の型（StoryContents 等）が含まれうるため、
 * ArticleSection のみをフィルタリングしてから抽出する。
 */
const extractExperienceIds = (sections: Contents): string[] => {
  const ids = sections
    .filter((section): section is ArticleSection => section.fieldId === 'articleSection' && Array.isArray(section.columns))
    .flatMap((section) => section.columns)
    .flatMap((column) => column.contents)
    .flatMap((content) => {
      if (content.fieldId === 'smallExps' || content.fieldId === 'mediumExps') {
        return content.experiences.map((e) => e.id);
      }
      if (content.fieldId === 'largeExps' && content.experience?.id) {
        return [content.experience.id];
      }
      return [];
    });

  return [...new Set(ids)];
};

/**
 * ArticleSection 内の体験をフィルターなしで取得する Composable
 *
 * CMS で指定された体験を destination / theme タグに関係なくそのまま取得する。
 *
 * 体験スラッグが存在しない場合は undefined を返し、
 * 呼び出し元で experiences（グリッドデータ）にフォールバックする。
 */
export const useArticleSectionExperiences = async (sections: Ref<Contents>) => {
  const ids = extractExperienceIds(sections.value);

  if (ids.length === 0) {
    return { experiences: undefined };
  }

  const path = computed(() => '/experiences/search');
  const query = computed(() => ({
    slugs: ids.join(','),
    perPage: '0',
    includes: 'experienceImages',
  }));

  const { data } = await useAsyncFetchFromPublicApi<WabunkaExperienceListResponse>({
    path,
    query,
    options: { server: true },
    converter: WabunkaExperienceListResponse.fromJSON,
  });

  const experiences = computed(() => {
    if (!data.value) return undefined;
    return buildWabunkaExperienceListViewModel(data.value.items);
  });

  return { experiences };
};
